import type { AxiosInstance } from "axios";

/** Mirrors apps/api's billing overview — apps/web cannot import server code. */
export interface PlanOption {
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly priceMinor: number;
  readonly currency: string;
  readonly interval: string;
  readonly trialDays: number;
  readonly reportKeys: readonly string[];
  readonly limits: Readonly<Record<string, unknown>>;
  readonly buyable: boolean;
  readonly current: boolean;
}

export interface CurrentPlan {
  readonly planCode: string;
  readonly planName: string;
  readonly status: string;
  readonly trialEndsAt: string | null;
  readonly trialExpired: boolean;
  readonly reportKeys: readonly string[];
  readonly limits: Readonly<Record<string, unknown>>;
}

export interface BillingOverview {
  readonly current: CurrentPlan | null;
  readonly plans: readonly PlanOption[];
}

export async function fetchBilling(client: AxiosInstance): Promise<BillingOverview> {
  const response = await client.get<{ success: boolean; data: BillingOverview }>("/billing/subscription");
  return response.data.data;
}

/**
 * Start a checkout. The API returns Razorpay's hosted checkout URL; the browser NAVIGATES
 * to it — a payment page is a full redirect the user completes, not something to fetch.
 */
export async function startCheckout(client: AxiosInstance, planCode: string): Promise<string> {
  const response = await client.post<{ success: boolean; data: { checkoutUrl: string } }>("/billing/checkout", {
    planCode,
  });
  return response.data.data.checkoutUrl;
}

/**
 * Price for display, in the plan's own currency. `Intl.NumberFormat` with the currency
 * gives the right symbol and grouping — ₹4,999.00 for INR — without us hardcoding a
 * symbol that would be wrong for a store billing in dollars.
 */
export function formatPrice(minor: number, currency: string): string {
  if (minor === 0) return "Free";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(minor / 100);
}
